import { useMemo, useRef, useState } from "react";
import {
  WAVE_REACTION_PALETTE,
  resolveCommentAuthorLabel,
  type WaveCommentRecord,
  type WaveGalleryItem,
  type WaveParticipantRecord,
  type WavePresenceRecord,
} from "@vida/wave-core";
import { WaveAuthorAvatar } from "../presence/WaveAuthorAvatar";
import { renderMentionAwareText } from "../../services/waveMentionText";
import { WaveMentionComposer } from "./WaveMentionComposer";

type Props = {
  comments: WaveCommentRecord[];
  blockAnchorId: string;
  participants: WaveParticipantRecord[];
  mentionParticipants?: WaveParticipantRecord[];
  presences?: WavePresenceRecord[];
  currentUserSub: string;
  readOnly?: boolean;
  galleryItemIdFilter?: string | null;
  galleryItems?: WaveGalleryItem[];
  pinComposeFooter?: boolean;
  onPost: (body: string, parentId: string | null) => void;
  onToggleReaction: (commentId: string, emoji: string) => void;
  onCollapse?: () => void;
};

type SortOrder = "newest" | "oldest";

function galleryItemCommentLabel(
  items: WaveGalleryItem[] | undefined,
  itemId: string,
): string {
  if (!items?.length) return "Immagine";
  const index = items.findIndex((i) => i.id === itemId);
  const item = index >= 0 ? items[index] : undefined;
  if (item?.fileName?.trim()) return item.fileName.trim();
  if (index >= 0) return `Immagine ${index + 1}`;
  return "Immagine";
}

function formatCommentTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("it-IT", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type CommentNode = {
  comment: WaveCommentRecord;
  replies: WaveCommentRecord[];
};

function replyTargetId(comment: WaveCommentRecord): string {
  return comment.parentId ?? comment.id;
}

function buildThread(comments: WaveCommentRecord[], sortOrder: SortOrder): CommentNode[] {
  const sorted = [...comments].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  const topLevel = sorted.filter((c) => !c.parentId);
  const repliesByParent = new Map<string, WaveCommentRecord[]>();
  for (const comment of sorted) {
    if (!comment.parentId) continue;
    const list = repliesByParent.get(comment.parentId) ?? [];
    list.push(comment);
    repliesByParent.set(comment.parentId, list);
  }
  const cmp =
    sortOrder === "newest"
      ? (a: WaveCommentRecord, b: WaveCommentRecord) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      : (a: WaveCommentRecord, b: WaveCommentRecord) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  return topLevel
    .sort(cmp)
    .map((comment) => ({
      comment,
      replies: [...(repliesByParent.get(comment.id) ?? [])].sort(cmp),
    }));
}

function isAuthorOnline(
  authorSub: string,
  presences: WavePresenceRecord[] | undefined,
): boolean {
  if (!presences?.length) return false;
  return presences.some((p) => p.sub === authorSub);
}

function ReactionBar({
  comment,
  currentUserSub,
  readOnly,
  onToggleReaction,
}: {
  comment: WaveCommentRecord;
  currentUserSub: string;
  readOnly?: boolean;
  onToggleReaction: (commentId: string, emoji: string) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const activeReactions = WAVE_REACTION_PALETTE.filter(
    (emoji) => (comment.reactions[emoji] ?? []).length > 0,
  );

  return (
    <div className="comment-reactions" role="group" aria-label="Reazioni">
      {activeReactions.map((emoji) => {
        const subs = comment.reactions[emoji] ?? [];
        const count = subs.length;
        const active = subs.includes(currentUserSub);
        return (
          <button
            key={emoji}
            type="button"
            className={`comment-reaction-btn${active ? " active" : ""}`}
            disabled={readOnly}
            aria-pressed={active}
            aria-label={`${emoji}, ${count}`}
            onClick={() => onToggleReaction(comment.id, emoji)}
          >
            <span className="comment-reaction-emoji">{emoji}</span>
            <span className="comment-reaction-count">{count}</span>
          </button>
        );
      })}
      {!readOnly && (
        <div className="comment-reaction-add-wrap" ref={pickerRef}>
          <button
            type="button"
            className="comment-reaction-add-btn"
            aria-label="Aggiungi reazione"
            aria-expanded={pickerOpen}
            onClick={() => setPickerOpen((v) => !v)}
          >
            +
          </button>
          {pickerOpen && (
            <div className="comment-reaction-picker" role="menu">
              {WAVE_REACTION_PALETTE.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  role="menuitem"
                  className="comment-reaction-picker-btn"
                  onClick={() => {
                    onToggleReaction(comment.id, emoji);
                    setPickerOpen(false);
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CommentItem({
  comment,
  participants,
  galleryItems,
  presences,
  currentUserSub,
  readOnly,
  isReply,
  replyOpen,
  replyDraft,
  mentionRoster,
  onReplyOpen,
  onReplyDraftChange,
  onReplySubmit,
  onToggleReaction,
}: {
  comment: WaveCommentRecord;
  participants: WaveParticipantRecord[];
  galleryItems?: WaveGalleryItem[];
  presences?: WavePresenceRecord[];
  currentUserSub: string;
  readOnly?: boolean;
  isReply?: boolean;
  replyOpen: boolean;
  replyDraft: string;
  mentionRoster: WaveParticipantRecord[];
  onReplyOpen: () => void;
  onReplyDraftChange: (v: string) => void;
  onReplySubmit: () => void;
  onToggleReaction: (commentId: string, emoji: string) => void;
}) {
  const authorLabel = resolveCommentAuthorLabel(comment, participants);
  const online = isAuthorOnline(comment.authorSub, presences);

  return (
    <div className={`comment-item${isReply ? " comment-item--reply" : ""}`}>
      <div className="comment-item-row">
        <div className="comment-item-avatar-wrap">
          <WaveAuthorAvatar
            authorSub={comment.authorSub}
            authorName={comment.authorName}
            participants={participants}
          />
          {online && <span className="comment-online-dot" aria-hidden />}
        </div>
        <div className="comment-item-main">
          <div className="comment-item-top">
            <div className="comment-item-meta">
              <span className="comment-author-name">{authorLabel}</span>
              <span className="comment-meta-sep" aria-hidden>
                ·
              </span>
              <time className="comment-time" dateTime={comment.createdAt}>
                {formatCommentTime(comment.createdAt)}
              </time>
            </div>
            {!readOnly && (
              <div className="comment-item-actions">
                <button type="button" className="comment-reply-btn" onClick={onReplyOpen}>
                  <span className="comment-reply-icon" aria-hidden>
                    ↩
                  </span>
                  {replyOpen ? "Annulla" : "Rispondi"}
                </button>
              </div>
            )}
          </div>
          {comment.galleryItemId && !isReply && (
            <span className="comment-gallery-badge">
              {galleryItemCommentLabel(galleryItems, comment.galleryItemId)}
            </span>
          )}
          <p className="comment-body">{renderMentionAwareText(comment.body)}</p>
          <ReactionBar
            comment={comment}
            currentUserSub={currentUserSub}
            readOnly={readOnly}
            onToggleReaction={onToggleReaction}
          />
        </div>
      </div>
      {replyOpen && !readOnly && (
        <WaveMentionComposer
          className="comment-compose comment-compose--inline"
          inputClassName="wave-title comment-compose-input"
          value={replyDraft}
          onChange={onReplyDraftChange}
          onSubmit={onReplySubmit}
          placeholder="Rispondi… usa @ per menzionare"
          ariaLabel="Risposta al commento"
          mentionRoster={mentionRoster}
          autoFocus
        />
      )}
    </div>
  );
}

export function WaveCommentThread({
  comments,
  blockAnchorId,
  participants,
  mentionParticipants,
  presences,
  currentUserSub,
  readOnly = false,
  galleryItemIdFilter,
  galleryItems,
  pinComposeFooter = false,
  onPost,
  onToggleReaction,
  onCollapse,
}: Props) {
  const [draft, setDraft] = useState("");
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");
  const mentionRoster = mentionParticipants ?? participants;

  const thread = useMemo(() => {
    let scoped = comments.filter((c) => c.blockAnchorId === blockAnchorId);
    if (galleryItemIdFilter) {
      scoped = scoped.filter((c) => c.galleryItemId === galleryItemIdFilter);
    }
    return buildThread(scoped, sortOrder);
  }, [comments, blockAnchorId, galleryItemIdFilter, sortOrder]);

  const submitTopLevel = () => {
    const body = draft.trim();
    if (!body) return;
    onPost(body, null);
    setDraft("");
  };

  const submitReply = () => {
    const body = replyDraft.trim();
    if (!body || !replyToId) return;
    onPost(body, replyToId);
    setReplyDraft("");
    setReplyToId(null);
  };

  const openReplyTo = (comment: WaveCommentRecord) => {
    const targetId = replyTargetId(comment);
    if (replyToId === targetId) {
      setReplyToId(null);
      setReplyDraft("");
      return;
    }
    setReplyToId(targetId);
    setReplyDraft("");
  };

  const totalCount = thread.reduce((n, node) => n + 1 + node.replies.length, 0);

  const insertAtCursor = (token: string) => {
    setDraft((prev) => (prev.endsWith(" ") || prev.length === 0 ? prev + token : `${prev} ${token}`));
  };

  return (
    <div
      className={`comment-thread blip-thread${pinComposeFooter ? " comment-thread--pinned-compose" : ""}`}
      aria-label="Commenti sul blocco"
    >
      <div className="comment-thread-header">
        <span className="comment-thread-title">
          <span className="comment-thread-title-icon" aria-hidden>
            💬
          </span>
          Commenti{totalCount > 0 ? ` (${totalCount})` : ""}
        </span>
        <div className="comment-thread-header-actions">
          <label className="comment-sort-control">
            <span className="comment-sort-label">Ordina:</span>
            <select
              className="comment-sort-select"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as SortOrder)}
              aria-label="Ordine commenti"
            >
              <option value="newest">più recenti</option>
              <option value="oldest">più vecchi</option>
            </select>
          </label>
          {onCollapse && (
            <button
              type="button"
              className="link-btn comment-collapse-btn"
              aria-label="Nascondi commenti"
              onClick={onCollapse}
            >
              Nascondi ▴
            </button>
          )}
        </div>
      </div>
      <div className="comment-thread-body">
        {thread.length === 0 ? (
          <p className="comment-empty blip-empty muted">Nessun commento ancora.</p>
        ) : (
          thread.map(({ comment, replies }) => (
            <div key={comment.id} className="comment-thread-group">
              <CommentItem
                comment={comment}
                participants={participants}
                galleryItems={galleryItems}
                presences={presences}
                currentUserSub={currentUserSub}
                readOnly={readOnly}
                mentionRoster={mentionRoster}
                replyOpen={replyToId === comment.id}
                replyDraft={replyDraft}
                onReplyOpen={() => openReplyTo(comment)}
                onReplyDraftChange={setReplyDraft}
                onReplySubmit={submitReply}
                onToggleReaction={onToggleReaction}
              />
              {replies.map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  participants={participants}
                  galleryItems={galleryItems}
                  presences={presences}
                  currentUserSub={currentUserSub}
                  readOnly={readOnly}
                  isReply
                  mentionRoster={mentionRoster}
                  replyOpen={false}
                  replyDraft={replyDraft}
                  onReplyOpen={() => openReplyTo(reply)}
                  onReplyDraftChange={setReplyDraft}
                  onReplySubmit={submitReply}
                  onToggleReaction={onToggleReaction}
                />
              ))}
            </div>
          ))
        )}
      </div>
      {!readOnly && (
        <div
          className="comment-thread-compose-footer"
          onPointerDown={() => {
            if (replyToId) {
              setReplyToId(null);
              setReplyDraft("");
            }
          }}
        >
          <div className="comment-compose-pill">
            <button type="button" className="comment-compose-icon-btn" aria-label="Allegato" disabled>
              📎
            </button>
            <div className="comment-compose-pill-field">
              <WaveMentionComposer
                className="comment-compose-pill-inner"
                inputClassName="comment-compose-pill-input"
                value={draft}
                onChange={setDraft}
                onSubmit={submitTopLevel}
                placeholder="Scrivi un commento… usa @ per menzionare"
                ariaLabel="Nuovo commento"
                mentionRoster={mentionRoster}
              />
            </div>
            <button
              type="button"
              className="comment-compose-icon-btn"
              aria-label="Emoji"
              disabled
            >
              😊
            </button>
            <button
              type="button"
              className="comment-compose-icon-btn"
              aria-label="Menziona"
              onClick={() => insertAtCursor("@")}
            >
              @
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
